import React from 'react';
import PropTypes from 'prop-types';
import Toggle from '../../Toggle/Toggle';
import { Icon } from '../../Icon/Icon';

/**
 * GigabotHeader - Header component for Gigabot chat panel
 *
 * Props:
 * - onClose: function - Callback when close button is clicked
 * - showHistory: boolean - Whether history panel is shown
 * - onToggleHistory: function - Callback to toggle history panel
 * - webSearchEnabled: boolean - Whether web search is enabled
 * - onWebSearchChange: function - Callback when web search is toggled
 * - showWebSearch: boolean - Whether to show web search toggle
 * - showActionProposals: boolean - Whether action proposals feature is enabled
 * - autoRunMode: string - Current auto-run mode ('off', 'turbo')
 * - onAutoRunModeChange: function - Callback when auto-run mode changes
 * - showAutoRunSettings: boolean - Whether auto-run settings menu is open
 * - onToggleAutoRunSettings: function - Callback to toggle auto-run settings
 */
export const GigabotHeader = ({
  onClose,
  showHistory,
  onToggleHistory,
  webSearchEnabled,
  onWebSearchChange,
  showWebSearch = false,
  showActionProposals = false,
  autoRunMode = 'off',
  onAutoRunModeChange,
  showAutoRunSettings = false,
  onToggleAutoRunSettings,
}) => {
  return (
    <div className='gigabot-header'>
      <div className='gigabot-header-left'>
        <h3>Gigabot</h3>
        {/* History toggle button */}
        <button
          className={`history-toggle-btn ${showHistory ? 'active' : ''}`}
          onClick={onToggleHistory}
          aria-label='Conversation history'
          title='View conversation history'
        >
          <Icon name='Clock' width={16} height={16} />
        </button>
      </div>
      <div className='gigabot-header-controls'>
        {showWebSearch && (
          <Toggle
            checked={webSearchEnabled}
            onChange={onWebSearchChange}
            label='Web Search'
            title='Enable web search'
            ariaLabel='Toggle web search'
          />
        )}

        {/* Auto-run settings - only show when action proposals are enabled */}
        {showActionProposals && (
          <div className='auto-run-settings'>
            <button
              className='settings-icon-btn'
              onClick={onToggleAutoRunSettings}
              aria-label='Auto-run settings'
              title='Configure auto-run'
            >
              <Icon name='Settings' />
            </button>

            {showAutoRunSettings && (
              <div className='auto-run-settings-menu'>
                <div className='auto-run-settings-menu__header'>
                  <span>Auto-Run Mode</span>
                </div>

                <button
                  className={`auto-run-settings-menu__item ${
                    autoRunMode === 'off'
                      ? 'auto-run-settings-menu__item--active'
                      : ''
                  }`}
                  onClick={() => onAutoRunModeChange('off')}
                >
                  Off
                </button>

                <button
                  className={`auto-run-settings-menu__item ${
                    autoRunMode === 'turbo'
                      ? 'auto-run-settings-menu__item--active'
                      : ''
                  }`}
                  onClick={() => onAutoRunModeChange('turbo')}
                >
                  Turbo
                </button>
              </div>
            )}
          </div>
        )}

        <button
          className='gigabot-close'
          onClick={onClose}
          aria-label='Close Gigabot'
        >
          ×
        </button>
      </div>
    </div>
  );
};

GigabotHeader.propTypes = {
  onClose: PropTypes.func.isRequired,
  showHistory: PropTypes.bool,
  onToggleHistory: PropTypes.func.isRequired,
  webSearchEnabled: PropTypes.bool,
  onWebSearchChange: PropTypes.func,
  showWebSearch: PropTypes.bool,
  showActionProposals: PropTypes.bool,
  autoRunMode: PropTypes.oneOf(['off', 'turbo']),
  onAutoRunModeChange: PropTypes.func,
  showAutoRunSettings: PropTypes.bool,
  onToggleAutoRunSettings: PropTypes.func,
};

export default GigabotHeader;
